/**
 * The page frame every view sits in: a title block and a fragment-addressed tab
 * strip.
 *
 * THE TAB STRIP TAKES HREFS AND A CLICK HANDLER, NEVER A PARSER. Which tab is
 * active is decided by `lib/router.ts`'s pure `parseStaffTab` /
 * `parseMasterDataTab` / `parseJobsTab` at the call site and handed here as
 * `activeId`; this component reads the URL for nothing. Same for the click: it
 * calls back with the event and the href, and the view applies
 * `isPlainLeftClick` -- so a cmd-click still opens a tab.
 */
import type { MouseEvent, ReactNode } from 'react';
import { cn } from '../../lib/utils';

export function PageHeader({ title, description, actions, className }: {
  title: string;
  description?: string;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h1 className="text-[1.375rem] font-semibold tracking-[-0.021em]">{title}</h1>
        {description === undefined ? null : (
          <p className="mt-1 max-w-2xl text-[0.8125rem] text-muted">{description}</p>
        )}
      </div>
      {actions === undefined ? null : <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export interface TabItem<Id extends string> {
  id: Id;
  label: string;
  href: string;
}

export function TabNav<Id extends string>({ label, items, activeId, onTabClick, className }: {
  /** The `aria-label` of the <nav>. DOM suite 1 finds this strip by it. */
  label: string;
  items: readonly TabItem<Id>[];
  activeId: Id;
  onTabClick: (event: MouseEvent<HTMLAnchorElement>, href: string) => void;
  className?: string;
}) {
  return (
    <nav aria-label={label} className={cn('border-b border-line', className)}>
      <ul className="-mb-px flex list-none flex-wrap gap-0 p-0">
        {items.map((item) => {
          const active = item.id === activeId;
          return (
            <li key={item.id}>
              <a
                href={item.href}
                aria-current={active ? 'page' : undefined}
                onClick={(event) => onTabClick(event, item.href)}
                className={cn(
                  'inline-flex items-center border-b-2 px-3 py-2 text-[0.8125rem] font-medium no-underline transition-colors',
                  active
                    ? 'border-accent text-text'
                    : 'border-transparent text-muted hover:border-line hover:text-text hover:no-underline',
                )}
              >
                {item.label}
              </a>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/** The vertical rhythm of a view: header, then stacked sections. */
export function PageBody({ className, ...props }: { className?: string; children: ReactNode }) {
  return <div className={cn('flex flex-col gap-5', className)} {...props} />;
}
